import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import DocumentManager from '../components/DocumentManager';
import ProjectFormModal from '../components/ProjectFormModal';
import { useAuth } from '../context/AuthContext';
import './ProjectDetail.css';
import { toast } from '../components/Toast';

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasMenuAccess } = useAuth();
  const canManage = ['COMPANY_ADMIN', 'BRANCH_MANAGER'].includes(user?.role) || hasMenuAccess('manage-projects');
  const [project, setProject] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [wbsItems, setWbsItems] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [supervisorUpdates, setSupervisorUpdates] = useState([]);
  const [photoBlobs, setPhotoBlobs] = useState({}); // { photoId: blobUrl }
  const [documents, setDocuments] = useState([]);
  const [profitability, setProfitability] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [projectStocks, setProjectStocks] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [transferPosts, setTransferPosts] = useState([]);
  const [transfersLoading, setTransfersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  
  // WBS form state
  const [showWBSForm, setShowWBSForm] = useState(false);
  const [showEditWBSForm, setShowEditWBSForm] = useState(false);
  const [wbsFormData, setWbsFormData] = useState({
    taskName: '',
    assignedToId: '',
    startDate: '',
    endDate: '',
    status: 'NOT_STARTED',
    description: '',
    parentId: ''
  });
  const [editingWBS, setEditingWBS] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchProjectDetails();
    fetchWBS();
    fetchExpenses();
    fetchSupervisorUpdates();
    fetchDocuments();
    fetchProfitability();
    fetchUsers();
    fetchProjectStocks();
    fetchTransferPosts();
  }, [id]);

  const fetchProjectStocks = async () => {
    setStockLoading(true);
    try {
      const res = await axios.get(`/projects/${id}/stock`);
      const data = res.data.data || res.data || [];
      setProjectStocks(data);
    } catch (err) {
      console.error(err);
      setProjectStocks([]);
    } finally {
      setStockLoading(false);
    }
  };

  const fetchTransferPosts = async () => {
    setTransfersLoading(true);
    try {
      const res = await axios.get(`/projects/${id}/transfer-posts`);
      const data = res.data.data || res.data || [];
      setTransferPosts(data);
    } catch (err) {
      console.error(err);
      setTransferPosts([]);
    } finally {
      setTransfersLoading(false);
    }
  };

  const fetchProjectDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/projects/${id}`);
      const projectData = response.data.data || response.data;
      setProject(projectData);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to fetch project details');
    } finally {
      setLoading(false);
    }
  };

  const fetchWBS = async () => {
    try {
      const response = await axios.get(`/projects/${id}/wbs`);
      const wbsData = response.data.data || response.data;
      setWbsItems(wbsData);
    } catch (err) {
      console.error('Failed to fetch WBS:', err);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await axios.get(`/projects/${id}/expenses`);
      const expenseData = response.data.data || response.data;
      setExpenses(expenseData);
    } catch (err) {
      console.error('Failed to fetch expenses:', err);
    }
  };

  const fetchSupervisorUpdates = async () => {
    try {
      const response = await axios.get(`/projects/${id}/supervisor-updates`);
      const updateData = response.data.data || response.data;

      // Fetch photo metadata for each update
      const updatesWithPhotos = await Promise.all(
        updateData.map(async (update) => {
          try {
            const photosResponse = await axios.get(`/supervisor-updates/${update.id}/photos`);
            const photos = photosResponse.data.data || [];
            return { ...update, photos };
          } catch {
            return { ...update, photos: [] };
          }
        })
      );

      setSupervisorUpdates(updatesWithPhotos);

      // Fetch each photo as a blob URL (auth header required)
      updatesWithPhotos.forEach(update => {
        (update.photos || []).forEach(photo => {
          axios.get(`/supervisor-updates/${update.id}/photos/${photo.id}/download`, { responseType: 'blob' })
            .then(r => {
              const url = URL.createObjectURL(r.data);
              setPhotoBlobs(prev => ({ ...prev, [photo.id]: url }));
            })
            .catch(() => {});
        });
      });
    } catch (err) {
      console.error('Failed to fetch supervisor updates:', err);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`/projects/${id}/documents`);
      const docData = response.data.data || response.data;
      setDocuments(docData);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };

  const fetchProfitability = async () => {
    try {
      const response = await axios.get(`/projects/${id}/profitability`);
      const profitData = response.data.data || response.data;
      setProfitability(profitData);
    } catch (err) {
      console.error('Failed to fetch profitability:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users?size=1000');
      const userData = response.data.data?.content || response.data.content || [];
      setUsers(userData);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleWBSFormChange = (e) => {
    const { name, value } = e.target;
    setWbsFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateWBS = async (e) => {
    e.preventDefault();
    
    // Validate end date is after start date
    if (wbsFormData.startDate && wbsFormData.endDate) {
      const startDate = new Date(wbsFormData.startDate);
      const endDate = new Date(wbsFormData.endDate);
      if (endDate <= startDate) {
        toast.error('End date must be after start date');
        return;
      }
    }
    
    try {
      const payload = {
        ...wbsFormData,
        projectId: parseInt(id),
        assignedToId: wbsFormData.assignedToId ? parseInt(wbsFormData.assignedToId) : null,
        parentId: wbsFormData.parentId ? parseInt(wbsFormData.parentId) : null
      };
      
      await axios.post(`/projects/${id}/wbs`, payload);
      setShowWBSForm(false);
      setWbsFormData({
        taskName: '',
        assignedToId: '',
        startDate: '',
        endDate: '',
        status: 'NOT_STARTED',
        description: '',
        parentId: ''
      });
      fetchWBS();
      showSuccessMessage('WBS item created successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create WBS item');
    }
  };

  const handleEditWBS = (wbs) => {
    setEditingWBS(wbs);
    setWbsFormData({
      taskName: wbs.taskName,
      assignedToId: wbs.assignedToId ? wbs.assignedToId.toString() : '',
      startDate: wbs.startDate ? wbs.startDate.split('T')[0] : '',
      endDate: wbs.endDate ? wbs.endDate.split('T')[0] : '',
      status: wbs.status,
      description: wbs.description || '',
      parentId: wbs.parentId ? wbs.parentId.toString() : ''
    });
    setShowEditWBSForm(true);
  };

  const handleUpdateWBS = async (e) => {
    e.preventDefault();
    
    // Validate end date is after start date
    if (wbsFormData.startDate && wbsFormData.endDate) {
      const startDate = new Date(wbsFormData.startDate);
      const endDate = new Date(wbsFormData.endDate);
      if (endDate <= startDate) {
        toast.error('End date must be after start date');
        return;
      }
    }
    
    try {
      const payload = {
        ...wbsFormData,
        assignedToId: wbsFormData.assignedToId ? parseInt(wbsFormData.assignedToId) : null,
        parentId: wbsFormData.parentId ? parseInt(wbsFormData.parentId) : null
      };
      
      await axios.put(`/projects/${id}/wbs/${editingWBS.id}`, payload);
      setShowEditWBSForm(false);
      setEditingWBS(null);
      setWbsFormData({
        taskName: '',
        assignedToId: '',
        startDate: '',
        endDate: '',
        status: 'NOT_STARTED',
        description: '',
        parentId: ''
      });
      fetchWBS();
      showSuccessMessage('WBS item updated successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update WBS item');
    }
  };

  const handleDeleteWBS = async (wbsId) => {
    if (!window.confirm('Are you sure you want to delete this WBS item?')) {
      return;
    }

    try {
      await axios.delete(`/projects/${id}/wbs/${wbsId}`);
      fetchWBS();
      showSuccessMessage('WBS item deleted successfully');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete WBS item');
    }
  };

  const showSuccessMessage = (message) => {
    toast.success(message);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      PLANNED: 'badge-planned',
      ACTIVE: 'badge-active',
      DELAYED: 'badge-delayed',
      COMPLETED: 'badge-completed',
      CANCELLED: 'badge-cancelled',
      NOT_STARTED: 'badge-not-started',
      IN_PROGRESS: 'badge-in-progress',
      BLOCKED: 'badge-blocked'
    };
    return statusClasses[status] || '';
  };

  if (loading) {
    return <div className="loading">Loading project details...</div>;
  }

  if (!project) {
    return (
      <div className="project-detail-container">
        <p style={{ margin: '20px 0' }}>Project not found.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/projects')}>
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="project-detail-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/projects')}>
            ← Back to Projects
          </button>
          <h1>Project Details</h1>
        </div>
        {canManage && (
          <button
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            onClick={() => setShowEditModal(true)}
          >
            <span>✎</span> Edit Project
          </button>
        )}
      </div>

      <div className="project-info-card">
        <div className="card-header">
          <h2>Project Information</h2>
          <span className={`badge ${getStatusBadgeClass(project.status)}`}>
            {project.status}
          </span>
        </div>
        <div className="info-grid">
          <div className="info-item">
            <label>Project ID</label>
            <span>{project.id}</span>
          </div>
          <div className="info-item">
            <label>Name</label>
            <span>{project.name}</span>
          </div>
          <div className="info-item">
            <label>Customer</label>
            <span>{project.customerName}</span>
          </div>
          <div className="info-item">
            <label>Branch</label>
            <span>{project.branchName || '-'}</span>
          </div>
          <div className="info-item">
            <label>Project Assignee</label>
            <span>{project.projectManagerName || '-'}</span>
          </div>
          <div className="info-item">
            <label>Start Date</label>
            <span>{new Date(project.startDate).toLocaleDateString()}</span>
          </div>
          <div className="info-item">
            <label>End Date</label>
            <span>{project.endDate ? new Date(project.endDate).toLocaleDateString() : '-'}</span>
          </div>
          <div className="info-item">
            <label>Budget</label>
            <span className="amount-budget">{formatCurrency(project.budget)}</span>
          </div>
          <div className="info-item">
            <label>Completion Date</label>
            <span>{project.completionDate ? new Date(project.completionDate).toLocaleDateString() : '-'}</span>
          </div>

          <div className="info-item full-width" style={{ marginTop: '8px', paddingTop: '12px', borderTop: '1px solid #f3f4f6' }}>
            <label style={{ fontWeight: '600', color: '#374151', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span>👥</span> Who Can Access This Project (Assigned Employees)
            </label>
            <div className="accessible-employees-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {project.assignedEmployees && project.assignedEmployees.length > 0 ? (
                project.assignedEmployees.map(emp => (
                  <span key={emp.id} className="employee-access-badge">
                    <span className="badge-avatar">{emp.name?.charAt(0).toUpperCase()}</span>
                    <span className="badge-name">{emp.name}</span>
                    {emp.employeeCode && <span className="badge-code">({emp.employeeCode})</span>}
                    {emp.designation && <span className="badge-desig">• {emp.designation}</span>}
                  </span>
                ))
              ) : project.assignedEmployeeNames && project.assignedEmployeeNames.length > 0 ? (
                project.assignedEmployeeNames.map((name, idx) => (
                  <span key={idx} className="employee-access-badge">
                    <span className="badge-avatar">{name.charAt(0).toUpperCase()}</span>
                    <span className="badge-name">{name}</span>
                  </span>
                ))
              ) : (
                <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '13px' }}>
                  {project.projectManagerName ? `Assigned to ${project.projectManagerName}` : 'No specific employees assigned (users with Project menu access can view)'}
                </span>
              )}
            </div>
          </div>

          {project.description && (
            <div className="info-item full-width">
              <label>Description</label>
              <p>{project.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Graphical Representation */}
      <div className="project-visual-summary">
        <div className="card-header">
          <h2>Financial Overview</h2>
        </div>
        
        <div className="visual-grid">
          {/* Budget vs Expenses Chart */}
          <div className="visual-card">
            <h3>Budget vs Expenses</h3>
            <div className="chart-container">
              {(() => {
                const budget = Number(project.budget || 0);
                const expensesTotal = Number(profitability?.totalExpenses || 0);
                const remaining = budget - expensesTotal;
                const expensePercent = budget > 0 ? Math.min((expensesTotal / budget) * 100, 100) : 0;
                const remainingPercent = budget > 0 ? Math.min(Math.max((remaining / budget) * 100, 0), 100) : 0;

                return (
                  <>
                    <div className="chart-bar-container">
                      <div className="chart-bar-label">Total Budget</div>
                      <div className="chart-bar-bg" title={`Total Budget: ${formatCurrency(budget)}`}>
                        <div 
                          className="chart-bar-fill budget"
                          style={{ width: '100%' }}
                        ></div>
                      </div>
                      <div className="chart-bar-value">{formatCurrency(budget)}</div>
                    </div>
                    
                    <div className="chart-bar-container">
                      <div className="chart-bar-label">Expenses</div>
                      <div className="chart-bar-bg" title={`Expenses: ${formatCurrency(expensesTotal)}`}>
                        <div 
                          className="chart-bar-fill expenses"
                          style={{ width: `${expensePercent}%` }}
                        ></div>
                      </div>
                      <div className="chart-bar-value">{formatCurrency(expensesTotal)}</div>
                    </div>
                    
                    <div className="chart-bar-container">
                      <div className="chart-bar-label">Remaining</div>
                      <div className="chart-bar-bg" title={`Remaining: ${formatCurrency(remaining)}`}>
                        <div 
                          className="chart-bar-fill remaining"
                          style={{ width: `${remainingPercent}%` }}
                        ></div>
                      </div>
                      <div className="chart-bar-value">{formatCurrency(remaining)}</div>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>

          {/* WBS Status Distribution */}
          <div className="visual-card">
            <h3>WBS Status</h3>
            <div className="status-distribution">
              {wbsItems.length > 0 ? (
                <>
                  <div className="status-item">
                    <div className="status-item-label">
                      <span className="status-badge not-started"></span>
                      <span>Not Started</span>
                    </div>
                    <span className="status-count-badge">
                      {wbsItems.filter(w => w.status === 'NOT_STARTED').length}
                    </span>
                  </div>
                  <div className="status-item">
                    <div className="status-item-label">
                      <span className="status-badge in-progress"></span>
                      <span>In Progress</span>
                    </div>
                    <span className="status-count-badge in-progress">
                      {wbsItems.filter(w => w.status === 'IN_PROGRESS').length}
                    </span>
                  </div>
                  <div className="status-item">
                    <div className="status-item-label">
                      <span className="status-badge completed"></span>
                      <span>Completed</span>
                    </div>
                    <span className="status-count-badge completed">
                      {wbsItems.filter(w => w.status === 'COMPLETED').length}
                    </span>
                  </div>
                  <div className="status-item">
                    <div className="status-item-label">
                      <span className="status-badge blocked"></span>
                      <span>Blocked</span>
                    </div>
                    <span className="status-count-badge blocked">
                      {wbsItems.filter(w => w.status === 'BLOCKED').length}
                    </span>
                  </div>
                </>
              ) : (
                <div className="no-data">No WBS items yet</div>
              )}
            </div>
          </div>

          {/* Profitability Progress */}
          <div className="visual-card">
            <h3>Profitability</h3>
            <div className="progress-circle">
              {profitability && profitability.budget && profitability.budget > 0 ? (
                <>
                  <svg viewBox="0 0 36 36" className="circular-chart">
                    <path
                      className="circle-bg"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                    <path
                      className={`circle ${profitability.profitabilityPercentage >= 0 ? 'positive' : 'negative'}`}
                      strokeDasharray={`${Math.min(Math.abs(profitability.profitabilityPercentage), 100)}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    />
                  </svg>
                  <div className="progress-text">
                    <span className="percentage">
                      {profitability.profitabilityPercentage?.toFixed(1)}%
                    </span>
                    <span className="label">Profit</span>
                  </div>
                </>
              ) : (
                <div className="no-data">No data available</div>
              )}
            </div>
            <div className="progress-details">
              <div className="detail-item">
                <span className="detail-label">Budget</span>
                <span className="detail-value">{formatCurrency(project.budget || 0)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Expenses</span>
                <span className="detail-value">{formatCurrency(profitability?.totalExpenses || 0)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Profit/Loss</span>
                <span className={`detail-value ${profitability?.profitability >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(profitability?.profitability || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tabs-container">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`tab-button ${activeTab === 'wbs' ? 'active' : ''}`}
            onClick={() => setActiveTab('wbs')}
          >
            WBS <span className="tab-count">{wbsItems.length}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'expenses' ? 'active' : ''}`}
            onClick={() => setActiveTab('expenses')}
          >
            Expenses <span className="tab-count">{expenses.length}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'updates' ? 'active' : ''}`}
            onClick={() => setActiveTab('updates')}
          >
            Supervisor Updates <span className="tab-count">{supervisorUpdates.length}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'profitability' ? 'active' : ''}`}
            onClick={() => setActiveTab('profitability')}
          >
            Profitability
          </button>
          <button
            className={`tab-button ${activeTab === 'stock' ? 'active' : ''}`}
            onClick={() => setActiveTab('stock')}
          >
            Site Stock <span className="tab-count">{projectStocks.length}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'transfers' ? 'active' : ''}`}
            onClick={() => setActiveTab('transfers')}
          >
            Material Transfers <span className="tab-count">{transferPosts.length}</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documents
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <div className="overview-tab">
              <div className="overview-stats">
                <div className="stat-card">
                  <label>Total WBS Items</label>
                  <span className="stat-value">{wbsItems.length}</span>
                </div>
                <div className="stat-card">
                  <label>Total Expenses</label>
                  <span className="stat-value">{expenses.length}</span>
                </div>
                <div className="stat-card">
                  <label>Supervisor Updates</label>
                  <span className="stat-value">{supervisorUpdates.length}</span>
                </div>
                <div className="stat-card">
                  <label>Documents</label>
                  <span className="stat-value">{documents.length}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'wbs' && (
            <div className="wbs-tab">
              <div className="tab-actions">
                <button
                  className="btn btn-primary"
                  onClick={() => setShowWBSForm(!showWBSForm)}
                >
                  {showWBSForm ? 'Cancel' : '+ Add WBS Item'}
                </button>
              </div>

              {showWBSForm && (
                <form className="wbs-form" onSubmit={handleCreateWBS}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Task Name *</label>
                      <input
                        type="text"
                        name="taskName"
                        value={wbsFormData.taskName}
                        onChange={handleWBSFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Assigned To</label>
                      <select
                        name="assignedToId"
                        value={wbsFormData.assignedToId}
                        onChange={handleWBSFormChange}
                      >
                        <option value="">Select User</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        name="startDate"
                        value={wbsFormData.startDate}
                        onChange={handleWBSFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        name="endDate"
                        value={wbsFormData.endDate}
                        onChange={handleWBSFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="status"
                        value={wbsFormData.status}
                        onChange={handleWBSFormChange}
                      >
                        <option value="NOT_STARTED">Not Started</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="BLOCKED">Blocked</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={wbsFormData.description}
                      onChange={handleWBSFormChange}
                      rows="2"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary">Create WBS Item</button>
                </form>
              )}

              {showEditWBSForm && (
                <form className="wbs-form" onSubmit={handleUpdateWBS}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Task Name *</label>
                      <input
                        type="text"
                        name="taskName"
                        value={wbsFormData.taskName}
                        onChange={handleWBSFormChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Assigned To</label>
                      <select
                        name="assignedToId"
                        value={wbsFormData.assignedToId}
                        onChange={handleWBSFormChange}
                      >
                        <option value="">Select User</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>{user.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Start Date</label>
                      <input
                        type="date"
                        name="startDate"
                        value={wbsFormData.startDate}
                        onChange={handleWBSFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>End Date</label>
                      <input
                        type="date"
                        name="endDate"
                        value={wbsFormData.endDate}
                        onChange={handleWBSFormChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="status"
                        value={wbsFormData.status}
                        onChange={handleWBSFormChange}
                      >
                        <option value="NOT_STARTED">Not Started</option>
                        <option value="IN_PROGRESS">In Progress</option>
                        <option value="COMPLETED">Completed</option>
                        <option value="BLOCKED">Blocked</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={wbsFormData.description}
                      onChange={handleWBSFormChange}
                      rows="2"
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="btn btn-primary">Update WBS Item</button>
                    <button 
                      type="button" 
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowEditWBSForm(false);
                        setEditingWBS(null);
                        setWbsFormData({
                          taskName: '',
                          assignedToId: '',
                          startDate: '',
                          endDate: '',
                          status: 'NOT_STARTED',
                          description: '',
                          parentId: ''
                        });
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}

              {wbsItems.length === 0 ? (
                <div className="no-data">No WBS items found</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Task Name</th>
                      <th>Assigned To</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wbsItems.map((wbs) => (
                      <tr key={wbs.id}>
                        <td>{wbs.taskName}</td>
                        <td>{wbs.assignedToName || '-'}</td>
                        <td>{wbs.startDate ? new Date(wbs.startDate).toLocaleDateString() : '-'}</td>
                        <td>{wbs.endDate ? new Date(wbs.endDate).toLocaleDateString() : '-'}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(wbs.status)}`}>
                            {wbs.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEditWBS(wbs)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteWBS(wbs.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'expenses' && (
            <div className="expenses-tab">
              {expenses.length === 0 ? (
                <div className="no-data">No expenses found for this project</div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Category</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Vendor</th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map((expense) => (
                      <tr key={expense.id}>
                        <td>{expense.id}</td>
                        <td>{expense.category}</td>
                        <td>{formatCurrency(expense.amount)}</td>
                        <td>{new Date(expense.expenseDate).toLocaleDateString()}</td>
                        <td>{expense.vendorName || '-'}</td>
                        <td>{expense.description || '-'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-secondary"
                            onClick={() => setSelectedExpense(expense)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'updates' && (
            <div className="updates-tab">
              <div className="updates-header">
                <h3>Supervisor Updates</h3>
                <button 
                  className="btn-primary"
                  onClick={() => navigate('/supervisor-updates/new')}
                >
                  + Create Update
                </button>
              </div>
              {supervisorUpdates.length === 0 ? (
                <div className="no-data">
                  <p>No supervisor updates found</p>
                  <button 
                    className="btn-primary"
                    onClick={() => navigate('/supervisor-updates/new')}
                  >
                    Create First Update
                  </button>
                </div>
              ) : (
                <div className="updates-timeline">
                  {supervisorUpdates.map((update) => (
                    <div key={update.id} className="update-card">
                      <div className="update-header">
                        <span className="update-date">
                          {new Date(update.updateDate).toLocaleDateString()}
                        </span>
                        <span className="update-progress">
                          Progress: {update.workProgress}%
                        </span>
                      </div>
                      <p className="update-description">{update.description}</p>
                      {update.photos && update.photos.length > 0 && (
                        <div className="update-photos">
                          <h4>Site Photos ({update.photos.length})</h4>
                          <div className="photos-grid">
                            {update.photos.map((photo) => (
                              <div key={photo.id} className="photo-thumbnail">
                                {photoBlobs[photo.id] ? (
                                  <img
                                    src={photoBlobs[photo.id]}
                                    alt={photo.fileName}
                                    onClick={() => window.open(photoBlobs[photo.id], '_blank')}
                                    style={{ cursor: 'pointer' }}
                                  />
                                ) : (
                                  <div className="photo-loading">⏳</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="update-footer">
                        <span>By: {update.createdByName}</span>
                        <span className="update-time">
                          {new Date(update.createdAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'profitability' && (
            <div className="profitability-tab">
              {profitability ? (
                <div className="profitability-cards">
                  <div className="profit-card">
                    <label>Budget</label>
                    <span className="profit-value">{formatCurrency(profitability.budget)}</span>
                  </div>
                  <div className="profit-card">
                    <label>Total Expenses</label>
                    <span className="profit-value expense">{formatCurrency(profitability.totalExpenses)}</span>
                  </div>
                  <div className="profit-card">
                    <label>Profitability</label>
                    <span className={`profit-value ${profitability.profitability >= 0 ? 'positive' : 'negative'}`}>
                      {formatCurrency(profitability.profitability)}
                    </span>
                  </div>
                  <div className="profit-card">
                    <label>Profitability %</label>
                    <span className={`profit-value ${profitability.profitabilityPercentage >= 0 ? 'positive' : 'negative'}`}>
                      {profitability.profitabilityPercentage?.toFixed(2)}%
                    </span>
                  </div>
                </div>
              ) : (
                <div className="no-data">Profitability data not available</div>
              )}
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="documents-tab">
              <DocumentManager 
                entityType="PROJECT" 
                entityId={id}
                canUpload={true}
                canDelete={true}
              />
            </div>
          )}

          {activeTab === 'transfers' && (
            <div className="stock-tab-content">
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold' }}>Project Material Transfers & Cost Allocation</h3>
              {transfersLoading ? (
                <div>Loading material transfers...</div>
              ) : transferPosts.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', border: '1px dashed #ccc', borderRadius: '6px' }}>
                  No material transfer cost entries exist for this project.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table" style={{ width: '100%', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th>Date</th>
                        <th>Voucher No</th>
                        <th>From / To</th>
                        <th>Item</th>
                        <th style={{ textAlign: 'right' }}>Quantity</th>
                        <th>Type</th>
                        <th style={{ textAlign: 'right' }}>Cost Value</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferPosts.map(post => (
                        <tr key={post.id}>
                          <td>{new Date(post.transferDate).toLocaleDateString()}</td>
                          <td><strong>{post.voucherNo}</strong></td>
                          <td>
                            <div style={{ fontSize: '12px' }}>
                              From: <strong>{post.fromName}</strong>
                            </div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                              To: <strong>{post.toName}</strong>
                            </div>
                          </td>
                          <td>
                            <strong>{post.itemName}</strong>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>{post.itemCode}</div>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {post.quantity} {post.unitOfMeasurement}
                          </td>
                          <td>
                            <span className={`badge badge-${post.type === 'EXPENSE' ? 'danger' : 'success'}`}>
                              {post.type === 'EXPENSE' ? 'Cost Added (EXPENSE)' : 'Cost Returned (INCOME)'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold', color: post.type === 'EXPENSE' ? '#ef4444' : '#10b981' }}>
                            {post.type === 'EXPENSE' ? '-' : '+'}{formatCurrency(post.amount)}
                          </td>
                          <td>{post.notes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'stock' && (
            <div className="stock-tab-content">
              <h3 style={{ margin: '0 0 15px 0', fontSize: '18px', fontWeight: 'bold' }}>Project Materials & Stock Levels</h3>
              {stockLoading ? (
                <div>Loading project stocks...</div>
              ) : projectStocks.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280', border: '1px dashed #ccc', borderRadius: '6px' }}>
                  No stocks are currently allocated to or used by this project.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table" style={{ width: '100%', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th>Item Code</th>
                        <th>Item Name</th>
                        <th style={{ textAlign: 'right' }}>Current (Available) Stock</th>
                        <th style={{ textAlign: 'right' }}>Used (Consumed) Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projectStocks.map(stock => (
                        <tr key={stock.id}>
                          <td><strong>{stock.itemCode}</strong></td>
                          <td>{stock.itemName}</td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold', color: parseFloat(stock.quantity) > 0 ? '#10b981' : '#6b7280' }}>
                            {stock.quantity} {stock.unitOfMeasurement}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold', color: parseFloat(stock.consumedQuantity) > 0 ? '#ef4444' : '#6b7280' }}>
                            {stock.consumedQuantity || 0} {stock.unitOfMeasurement}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {selectedExpense && (
        <div className="ic-modal-overlay" onClick={() => setSelectedExpense(null)}>
          <div className="ic-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="ic-modal-header" style={{ borderBottom: '1px solid #e5e7eb', padding: '20px 24px' }}>
              <h2>Expense Details</h2>
              <button className="ic-modal-close" onClick={() => setSelectedExpense(null)}>×</button>
            </div>
            
            <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Transaction ID</span>
                <span style={{ fontSize: '14px', color: '#111827', fontWeight: '600' }}>#{selectedExpense.id}</span>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Amount</span>
                <span style={{ fontSize: '16px', color: '#dc2626', fontWeight: '700' }}>
                  {formatCurrency(selectedExpense.amount)}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Date</span>
                <span style={{ fontSize: '14px', color: '#111827' }}>
                  {new Date(selectedExpense.expenseDate).toLocaleDateString()}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Category</span>
                <span style={{ fontSize: '14px', color: '#111827' }}>{selectedExpense.category || '-'}</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Vendor</span>
                <span style={{ fontSize: '14px', color: '#111827' }}>{selectedExpense.vendorName || '-'}</span>
              </div>

              <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Notes / Description</span>
                <span style={{ fontSize: '14px', color: '#4b5563', fontStyle: selectedExpense.description ? 'normal' : 'italic' }}>
                  {selectedExpense.description || 'No description provided'}
                </span>
              </div>

              {selectedExpense.items && selectedExpense.items.length > 0 && (
                <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                  <span style={{ fontSize: '12px', color: '#6b7280', fontWeight: '500', textTransform: 'uppercase' }}>Items ({selectedExpense.items.length})</span>
                  <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px' }}>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse', textAlign: 'left' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ padding: '8px 12px', fontWeight: '600' }}>Item Code & Name</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600', textAlign: 'right' }}>Qty</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600', textAlign: 'right' }}>Price</th>
                          <th style={{ padding: '8px 12px', fontWeight: '600', textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedExpense.items.map(item => (
                          <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ fontWeight: '500' }}>{item.itemName}</div>
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>{item.itemCode}</div>
                              {item.notes && <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>Notes: {item.notes}</div>}
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>{parseFloat(item.quantity).toFixed(2)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: '500' }}>{formatCurrency(item.totalAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div className="ic-modal-actions" style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
              <button type="button" className="ic-btn-secondary" onClick={() => setSelectedExpense(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <ProjectFormModal
          project={project}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchProjectDetails();
            toast.success('Project updated successfully');
          }}
        />
      )}
    </div>
  );
}

export default ProjectDetail;
